import queryString from 'querystring';

import classNames from 'classnames';
import { getSearch, push } from 'connected-react-router';
import { IMarkerProps, Map } from 'google-maps-react';
import parse from 'html-react-parser';
import { isEmpty } from 'lodash';
import Carousel from 'nuka-carousel';
import { FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { usePrevious, useUpdateEffect } from 'react-use';

import Prices from './Prices';
import PricingDetails from './PricingDetails';
import SuggestProduct from './SuggestProduct';
import TechnicalInformation from './TechnicalInformation';
import './index.scss';

import { ReactComponent as IconArrow } from '../../../../assets/images/arrow-left.svg';
import { ReactComponent as SliderLeft } from '../../../../assets/images/circle_paging_left.svg';
import { ReactComponent as SliderRight } from '../../../../assets/images/circle_paging_right.svg';
import { ReactComponent as IconInfo } from '../../../../assets/images/info.svg';
import { GetProductDetailsModel, ProductTypes } from '../../../../models/products.model';
import { updateQueryString } from '../../../../providers/location.provider';
import {
    bookingYear,
    dataNeighborsProducts,
    dataProductFull,
    getNeighborsProducts,
    getProductDetails,
} from '../../../../store/location';
import { AppState } from '../../../../store/store';
import { loadersByName, popupByName, togglePopup } from '../../../../store/ui';
import { memoizedGetPaths } from '../../../../utils';
import { openInNewTab } from '../../../../utils/urls';
import { useMedia } from '../../../context/InterfaceContext';
import { BASE_PATH, LOCATION_PATH, PRODUCT_PATH } from '../../Navigator';
import AddButton from '../../shared/addButton';
import Available from '../../shared/available';
import Button, { ButtonKind, ButtonSize } from '../../shared/button/Button';
import Checker from '../../shared/checker';
import Divider from '../../shared/Divider';
import DownloadFactSheet from '../../shared/downloadFactsheet';
import { InfoWindow, Marker, Polygon } from '../../shared/gmap/index';
import Id from '../../shared/id';
import { IMarker, Position } from '../../shared/mapContainer';
import Price from '../../shared/Price';
import styles from '../../shared/product/product.module.scss';
import RequestLocation from '../../shared/requestLocation';
import { scrollTo } from '../../shared/scrollTo';
import StrongText from '../../shared/StrongText';

const POLYGON_COLOR = '#B30200';

const Product: FC<{ location: Location }> = ({ location }) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const searchState = useSelector((state: AppState) => getSearch(state));
    const { data: productData = null, centerCordsMap: cords = undefined } = useSelector(dataProductFull);
    const dataNeighborsProductsState = useSelector(dataNeighborsProducts);
    const { opened: isOpenedFilter } = useSelector(popupByName('location_filter'));
    const productLoading = useSelector(loadersByName('product'));
    // const urlHistoryState = useSelector(urlHistory);
    const { search, pathname } = location;
    const parsedSearch = queryString.parse(search.replace('?', ''));
    if (parsedSearch.urn?.includes('?fbclid=')) {
        parsedSearch.urn = (parsedSearch.urn as string).split('?fbclid=')[0];
    }
    const isProductPage = pathname.includes('/location/product');
    const prevUrn = usePrevious(parsedSearch.urn);
    const { isMobileOrTablet, isFullMobile } = useMedia();
    const [activeMarker, setActiveMarker] = useState<IMarker>();
    const bookingyear = useSelector(bookingYear);
    const mapRef = useCallback(
        node => {
            if (node !== null) {
                parsedSearch && parsedSearch.scrolltomap === 'true' && scrollTo({ id: 'productDetailsMap', ref: node });
            }
        },
        [parsedSearch]
    );
    const isSatelliteView = parsedSearch && parsedSearch.mapviewsatellite === 'true';
    const isShowRequestModal = parsedSearch && parsedSearch.showrequest === 'true';
    const zoomLevel = parsedSearch.zoom ? Number(parsedSearch.zoom) : 0;
    const bookingYearFromUrl = parsedSearch.bookingyear ? Number(parsedSearch.bookingyear) : null;

    const preparePayload = useCallback(
        (): GetProductDetailsModel => ({
            bookingyear: bookingYearFromUrl ? bookingYearFromUrl : bookingyear,
            urn: parsedSearch.urn as string,
        }),
        [bookingYearFromUrl, bookingyear, parsedSearch.urn]
    );

    const getProducts = useCallback(() => {
        window.scrollTo(0, 0);
        dispatch(getProductDetails(preparePayload()));
        dispatch(getNeighborsProducts(preparePayload()));
    }, [dispatch, preparePayload]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (
            isProductPage &&
            (!parsedSearch.urn?.includes(String(productData?.number)) || parsedSearch.urn != productData?.producturn)
        ) {
            getProducts();
        }
    }, [isProductPage, parsedSearch, getProducts, productData?.number]);
    /* eslint-enable react-hooks/exhaustive-deps */

    useUpdateEffect(() => {
        if (isProductPage && parsedSearch.urn !== prevUrn) {
            getProducts();
        }
    }, [parsedSearch, prevUrn, getProducts, isProductPage]);

    useEffect(() => {
        if (isShowRequestModal && productData) {
            const { number, title, requestofferbutton } = productData;
            const [format, address] = title.split('<br>');

            dispatch(
                togglePopup({
                    name: 'location_request',
                    status: true,
                    payload: [
                        {
                            format,
                            address,
                            id: number,
                            requestofferbutton: requestofferbutton,
                            urn: parsedSearch.urn as string,
                        },
                    ],
                })
            );
        }
    }, [parsedSearch, productData, isShowRequestModal, dispatch]);

    const textOfFields = (
        {
            iinfo,
            label,
            value,
        }: {
            iinfo: { icon: boolean | null; link: string | undefined; text: string | undefined } | undefined;
            label: string;
            value: number | string;
        },
        showWithEmptyLabel: boolean
    ) => {
        if (value && !label.trim() && showWithEmptyLabel) {
            return (
                <p className="description-field" key={`field-${label}-${value}`}>
                    {value}
                </p>
            );
        }

        if (value && label.trim()) {
            return (
                <p className="description-field" key={`field-${label}-${value}`}>
                    <b>
                        {parse(label)}
                        {intl.locale === 'fr' ? ' ' : ''}:
                    </b>{' '}
                    {parse(value.toString())}
                    {iinfo?.icon && (
                        <>
                            <span className="info-icon" data-tip={iinfo.text || ''}>
                                {' '}
                                <IconInfo width={'14px'} height={'14px'} style={{ position: 'relative', top: '-3px' }} />
                            </span>
                            <ReactTooltip className="tooltip-iinfo" place="top" effect="solid" />
                        </>
                    )}
                </p>
            );
        }

        return null;
    };

    const renderFields = (
        data: Array<{
            iinfo: { icon: boolean | null; link: string | undefined; text: string | undefined } | undefined;
            label: string;
            value: string | number;
        }> | null,
        showWithEmptyLabel = false
    ) => !!data && !isEmpty(data) && data.map(item => textOfFields(item, showWithEmptyLabel));

    const handleBack = () => {
        if (window.history.length <= 1) {
            dispatch(
                push({
                    pathname: LOCATION_PATH,
                    search: updateQueryString(searchState, { urn: undefined }),
                })
            );
        } else {
            history.back();
        }
        /*const length = urlHistoryState.length;
        const item = length > 1 ? urlHistoryState[length - 2] : null;

        if (item?.includes('/location') && !item?.includes('/location/product')) {
            history.back();
        } else {
            dispatch(
                push({
                    pathname: LOCATION_PATH,
                    search: updateQueryString(searchState, { urn: undefined }),
                })
            );
        }*/
    };

    const getFormat = () => {
        if (productData && productData.maininfo) {
            // ToDo: check for the 'Format' word fails on Italian language
            const formatInfo = productData.maininfo.filter(item => item.label === 'Format' || item.label === 'Formato');
            return formatInfo ? formatInfo[0].value : null;
        }
        return null;
    };

    if (productData && productData.number && productData.productnumber && !productLoading && parsedSearch.urn) {
        const {
            validfrom,
            validto,
            productimage,
            number,
            productnumber,
            title,
            technicalinfo,
            maininfo,
            pricedetails,
            productimages,
            priceinformation,
            pricedays,
            prices,
            location,
            type,
            pricetabletext,
            requestofferbutton,
            formatcode,
        } = productData;
        const [format, address] = title.split('<br>');
        const paths = memoizedGetPaths(productData) as Position[][];
        const subPoints = productData.subpoints;
        const isExistPaths = !isEmpty(paths);
        const isExistSubPoints = !isEmpty(subPoints);
        const productFormat = getFormat();
        const isTMS = productFormat ? productFormat === 'TrafficMediaScreen' : false;
        const isTraffic = type === ProductTypes.TRAFFIC;
        const isTrafficOutdoor = type === ProductTypes.TRAFFIC_OUT_DOOR;
        const enableAirportMap = true;

        // Hide for not as per client request

        // // map zoom dictated by map bounds for digital products
        // let minLat = isExistSubPoints ? subPoints[0].y : 0;
        // let maxLat = isExistSubPoints ? subPoints[0].y : 0;
        // let minLng = isExistSubPoints ? subPoints[0].x : 0;
        // let maxLng = isExistSubPoints ? subPoints[0].x : 0;
        // subPoints.forEach(marker => {
        //     minLat = Math.min(minLat, marker.y);
        //     maxLat = Math.max(maxLat, marker.y);
        //     minLng = Math.min(minLng, marker.x);
        //     maxLng = Math.max(maxLng, marker.x);
        // });
        // const bounds = {
        //     ne: { lat: maxLat, lng: maxLng },
        //     sw: { lat: minLat, lng: minLng },
        // };
        // const googleBounds = new window.google.maps.LatLngBounds(
        //     new window.google.maps.LatLng(bounds.sw.lat, bounds.sw.lng),
        //     new window.google.maps.LatLng(bounds.ne.lat, bounds.ne.lng)
        // );
        //
        const zoomBase = zoomLevel ? zoomLevel : isExistSubPoints || isExistPaths ? 10 : 14;
        const zoomDigiflex = type === ProductTypes.DIGITAL ? 17 : null;
        const zoom = zoomDigiflex ? zoomDigiflex : zoomBase;

        return (
            <div className={'product-page__wrapper'} style={{ display: isOpenedFilter ? 'none' : 'flex' }}>
                <Button kind={ButtonKind.LINK} className={'product-page__back-button'} onClick={handleBack}>
                    <IconArrow />
                    {intl.formatMessage({ id: 'general.backButton', defaultMessage: 'Zurück' })}
                </Button>

                <section className="product-page__desc-wrapper">
                    <div className="product-page__desc-wrapper--image">
                        {productimages && productimages.length > 1 ? (
                            <Carousel
                                renderCenterLeftControls={({ previousSlide, currentSlide }) => {
                                    if (currentSlide !== 0) {
                                        return <SliderLeft onClick={previousSlide} />;
                                    } else {
                                        return null;
                                    }
                                }}
                                renderBottomCenterControls={null}
                                renderCenterRightControls={({ nextSlide, currentSlide, slideCount }) => {
                                    if (currentSlide !== slideCount - 1) {
                                        return <SliderRight onClick={nextSlide} />;
                                    } else {
                                        return null;
                                    }
                                }}
                            >
                                {productimages.map((productImage, index) => (
                                    <img
                                        src={`${productImage}&resolution=preview`}
                                        alt="product image"
                                        key={`productImage-${index}`}
                                    />
                                ))}
                            </Carousel>
                        ) : (
                            <img src={`${productimage}&resolution=preview`} alt="product image" />
                        )}

                        <AddButton className={styles.add} urn={parsedSearch.urn as string} removable={false} />
                    </div>

                    <div className="product-page__desc-wrapper--description">
                        <StrongText className={'description-name'}>{parse(title) as never}</StrongText>

                        <div className={classNames(styles.rowId, 'description-row-id')}>
                            <Id id={String(productnumber)} intl={intl} styles={{ marginRight: 18 }} />
                            <Available from={validfrom} to={validto} intl={intl} />
                        </div>

                        {renderFields(maininfo)}

                        <Divider size={2} margin={'25px 0'} />

                        <div className={'description-row-links'}>
                            <DownloadFactSheet urns={[parsedSearch.urn as string]} isFactSheetLabel />
                            <Checker productdata={productData} />
                        </div>

                        <div className={classNames(styles.rowId, 'description-row')}>
                            <Price
                                className={'price-field'}
                                priceinformation={priceinformation}
                                withGrossWithoutTage
                                duration={pricedays}
                                withEmptyText
                                intl={intl}
                                font={40}
                                margin={'0 0 20px 0'}
                                weight={500}
                            />

                            <RequestLocation
                                data={[
                                    {
                                        urn: parsedSearch.urn as string,
                                        format,
                                        address,
                                        id: number,
                                        requestofferbutton: requestofferbutton,
                                    },
                                ]}
                                showLink={true}
                            />
                        </div>
                    </div>
                </section>

                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                <Prices intl={intl} prices={prices} pricingText={pricetabletext} />
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                <PricingDetails intl={intl} data={pricedetails} pricingText={pricetabletext} />

                <section className="product-page__map-wrapper" id="productDetailsMap" ref={mapRef}>
                    <div className="product-page__map-wrapper--desc">
                        <h3>
                            {intl.formatMessage({ id: 'product.locationTitle', defaultMessage: 'Standortinformationen' })}
                        </h3>

                        {renderFields(location, type === ProductTypes.TRAFFIC || type === ProductTypes.TRAFFIC_OUT_DOOR)}
                    </div>

                    <div className="product-page__map-wrapper--map">
                        {google && cords && (enableAirportMap ? !productData.maplink : true) && (
                            <Map
                                google={google}
                                zoom={zoom}
                                // bounds={type === ProductTypes.DIGITAL ? googleBounds : undefined}
                                center={cords}
                                initialCenter={cords}
                                containerStyle={{ position: 'relative' }}
                                onReady={(_mapProps, map) => {
                                    map && map.setMapTypeId(isSatelliteView ? 'hybrid' : 'roadmap');
                                }}
                            >
                                {isExistPaths && (isTraffic || isTrafficOutdoor) ? (
                                    [
                                        <Polygon
                                            paths={paths}
                                            strokeColor={POLYGON_COLOR}
                                            strokeWeight={2}
                                            fillColor={POLYGON_COLOR}
                                            fillOpacity={0.2}
                                            key={`polygon-${number}`}
                                        />,
                                        <Marker
                                            key={`marker-${number}`}
                                            position={cords}
                                            title={title}
                                            name={number}
                                            useCustomMarker
                                        />,
                                    ]
                                ) : isExistSubPoints ? (
                                    subPoints.map((point, idx) => {
                                        const pointName = point.urn ? point.urn : `${point.name}-${idx}`;
                                        return [
                                            <Marker
                                                onClick={(_props?: IMarkerProps, marker?: IMarker) => {
                                                    setActiveMarker(marker);
                                                }}
                                                key={`marker-${point.x}-${point.y}-${point.name}`}
                                                position={{ lat: point.y, lng: point.x }}
                                                title={point.name}
                                                name={pointName}
                                                useCustomMarker
                                            />,
                                            <InfoWindow
                                                marker={activeMarker}
                                                key={`subpoint-${idx}`}
                                                id={`subpoint-${idx}`}
                                                google={google}
                                                map={google.maps}
                                                onClose={() => setActiveMarker(undefined)}
                                                visible={activeMarker && activeMarker.name === pointName}
                                            >
                                                <StrongText className={styles.formatHeaderSubpointPopup}>
                                                    {point.format}
                                                </StrongText>
                                                {point.image && (
                                                    <div className={styles.imageWrapper}>
                                                        <img
                                                            src={`${point.image}&resolution=thumbnail`}
                                                            alt="Product image"
                                                        />
                                                        {point.number && (
                                                            <p className={styles.imageNumber}>{point.number}</p>
                                                        )}
                                                    </div>
                                                )}
                                                <div className={styles.addressSubpointPopup}>{point.name}</div>
                                                {point.urn && (
                                                    <div id="infoButtonsWrapper">
                                                        {point.urn && (
                                                            <a
                                                                href={`${BASE_PATH}${PRODUCT_PATH.substring(
                                                                    1
                                                                )}?${updateQueryString(searchState, {
                                                                    urn: point.urn,
                                                                })}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                <Button
                                                                    kind={ButtonKind.INVERT}
                                                                    size={ButtonSize.FULL}
                                                                    id={`productView${point.urn}`}
                                                                >
                                                                    {intl.formatMessage({
                                                                        id: 'map.viewDetails',
                                                                        defaultMessage: 'Details anzeigen',
                                                                    })}
                                                                </Button>
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                            </InfoWindow>,
                                        ];
                                    })
                                ) : (
                                    <Marker position={cords} title={title} name={number} useCustomMarker />
                                )}
                            </Map>
                        )}
                        {productData.maplink && enableAirportMap && (
                            <div className="airport-map-wrapper">
                                <div
                                    className="airport-map"
                                    onClick={() => openInNewTab(productData.singlemaplink)}
                                    data-tip={intl.formatMessage({
                                        id: 'map.airport.tooltip',
                                        defaultMessage: 'Klicken Sie hier, um weitere Flächen auf der Karte anzuzeigen',
                                    })}
                                >
                                    <div className={'iconWrapper'}>
                                        <img
                                            alt={''}
                                            className={'iconEnlarge'}
                                            src="data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2018%2018%22%3E%3Cpath%20fill%3D%22%23666%22%20d%3D%22M0%200v6h2V2h4V0H0zm16%200h-4v2h4v4h2V0h-2zm0%2016h-4v2h6v-6h-2v4zM2%2012H0v6h6v-2H2v-4z%22/%3E%3C/svg%3E"
                                        />
                                    </div>
                                </div>
                                <ReactTooltip />
                                <iframe src={productData.maplink} width={'100%'} height={'100%'} />
                            </div>
                        )}
                    </div>
                </section>

                <TechnicalInformation
                    intl={intl}
                    data={technicalinfo}
                    dataForModal={[
                        {
                            urn: parsedSearch.urn as string,
                            format,
                            address,
                            id: number,
                            requestofferbutton: requestofferbutton,
                        },
                    ]}
                    productFormatCode={formatcode}
                />

                {!isEmpty(dataNeighborsProductsState) && (
                    <>
                        <Divider size={2} margin={isMobileOrTablet ? '0 0 40px' : '0 0 70px'} />

                        <section className="product-page__suggesting-wrapper">
                            <h3 className="product-page__suggesting-wrapper--title">
                                {intl.formatMessage({
                                    id: 'product.suggestingTitle',
                                    defaultMessage: 'Andere Formate in der Nähe',
                                })}
                            </h3>

                            <div className="product-page__suggesting-wrapper--products">
                                {isFullMobile ? (
                                    <Carousel
                                        heightMode={'max'}
                                        renderCenterLeftControls={null}
                                        renderCenterRightControls={null}
                                    >
                                        {dataNeighborsProductsState.map((item, i) => (
                                            <SuggestProduct key={`suggest-${item.urn}-${i}`} {...item} />
                                        ))}
                                    </Carousel>
                                ) : (
                                    dataNeighborsProductsState.map((item, i) => (
                                        <SuggestProduct key={`suggest-${item.urn}-${i}`} {...item} />
                                    ))
                                )}
                            </div>
                        </section>
                    </>
                )}
            </div>
        );
    } else {
        return productLoading ? null : (
            <div className={'product-page__wrapper'} style={{ display: isOpenedFilter ? 'none' : 'flex' }}>
                <Button kind={ButtonKind.LINK} className={'product-page__back-button'} onClick={handleBack}>
                    <IconArrow />
                    {intl.formatMessage({ id: 'general.backButton', defaultMessage: 'Zurück' })}
                </Button>
            </div>
        );
    }
};

export default Product;
